import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { SimilarDatasetsComponent } from '../../../shared/components/similar-datasets/similar-datasets.component';
import { ModelCardData } from '../../../shared/components/model-card/model-card.component';
import { ModalComponent } from '../../../shared/components/primeng/modal/modal.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { AssetDiscussionListComponent } from '../../../shared/components/asset-discussion-list/asset-discussion-list.component';
import { DiscussionItem } from '../../discussions/discussion-home/discussion-home.component';
import { CatalogueApiService, UserInteractionApiService, SearchCriteria, DiscussionApiService, RolesApiService, Role, FileApiService, AclApdApiService, AuthApiService } from '@angular-workspace/services';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { ModelCardMapperService } from '../../../shared/services/model-card-mapper.service';
import { ToasterService } from '../../../services/toaster.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { RequestAccessComponent } from '../../../shared/components/popups/request-access/request-access.component';
import { normalizeAclAccessStatus } from '../../normalize-acl-access-status';
import { parseItemAuditEnabled } from '../../../shared/utils/catalogue-asset-detail-navigation.util';
import { ratingStarFraction } from '../../../shared/utils/user-feedback.util';
import {
  ApiExplorerComponent,
  type ApiExplorerContext,
  splitFeedbackItems,
  type AdditionalInfoFaqRow,
} from '@angular-workspace/feature';

interface ModelReviewItem {
  id: string;
  userId: string;
  email: string;
  name: string;
  organisation: string;
  initials: string;
  comment: string;
  ratingValue: number;
  createdAt: string;
  createdAtMs: number;
  status: 'pending' | 'approved' | 'rejected';
}

@Component({
  selector: 'app-model-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    SimilarDatasetsComponent,
    ModalComponent,
    LoginModalComponent,
    AssetDiscussionListComponent,
    RequestAccessComponent,
    ApiExplorerComponent,
  ],
  templateUrl: './detail.component.html',
  styleUrl: './detail.component.scss',
})
export class ModelDetailComponent implements OnInit, OnDestroy {
  private catalogueApiService = inject(CatalogueApiService);
  private fileApiService = inject(FileApiService);
  private aclApdApiService = inject(AclApdApiService);
  private authApiService = inject(AuthApiService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private helperService = inject(HelperService);
  private formatService = inject(FormatService);
  private modelCardMapper = inject(ModelCardMapperService);
  private keycloak = inject(KeycloakService);
  private userInteractionApiService = inject(UserInteractionApiService);
  private rolesApiService = inject(RolesApiService);
  private toasterService = inject(ToasterService);
  private workspaceContext = inject(WorkspaceContextService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private discussionApiService = inject(DiscussionApiService);
  private keycloakSubscription?: Subscription;

  modelId: string | null = null;
  /** When true, initial item GET records a catalogue view audit (from catalogue navigation only). */
  private itemAuditEnabled = false;
  modelDiscussions: DiscussionItem[] = [];
  modelDiscussionsLoading = false;
  modelDiscussionsError: string | null = null;
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Models', route: '/models' },
      { label: 'Model Details' }
    ],
    title: 'Model Details',
    description: 'Loading model information...'
  };
  similarModels: ModelCardData[] = [];
  modelDescription: string = '';
  modelTags: string[] = [];
  showTagsModal: boolean = false;
  readonly MAX_VISIBLE_TAGS = 8;

  modelDetails: {
    uploadedBy?: string;
    organisationType?: string;
    industry?: string;
    organizationName?: string;
    lastUpdated?: string;
    license?: string;
    modelType?: string;
    programmingLanguage?: string;
    downloadCount?: number;
    viewCount?: number;
    githubRepo?: string;
    mediaUrl?: string;
    [key: string]: any;
  } = {};

  modelCardDetails: {
    badge?: string;
    createdAt?: string;
    modelType?: string;
    programmingLanguage?: string;
    fileFormat?: string;
    downloadCount?: number;
    viewCount?: number;
  } = {};

  // Store full model data to access publishStatus and other fields
  fullModelData: any = null;

  // Activity Overview metrics (from Item API)
  activityMetrics = {
    likes: 0,
    downloads: 0,
    views: 0,
    bookmarks: 0
  };

  isLoggedIn: boolean = false;
  isBookmarked: boolean = false;
  isLiked: boolean = false;
  showLoginModal: boolean = false;
  showGiveRatingModal = false;
  selectedRating = 0;
  hoveredRating = 0;
  ratingFeedback = '';
  ratingFormSubmitted = false;
  isSubmittingRating = false;
  isViewingOwnRating = false;
  isEditingOwnRating = false;
  private editBaselineRating = 0;
  private editBaselineFeedback = '';
  myModelReview: ModelReviewItem | null = null;
  ownSubmittedReview: ModelReviewItem | null = null;
  userFeedbackItems: ModelReviewItem[] = [];
  userFeedbackSummary: { averageRating: number | null; totalRatings: number } | null = null;
  userRatingsSort: 'new' | 'old' = 'new';
  userRatingsFilter: number | null = null;
  userRatingsPage = 0;
  showUserRatingsFilterMenu = false;
  private userFeedbackRequest = 0;
  readonly userRatingsPageSize = 3;
  readonly ratingFeedbackMax = 200;
  showRequestAccessModal: boolean = false;
  showApiExplorer = false;
  apiExplorerContext: ApiExplorerContext | null = null;
  isLoading: boolean = true;

  accessStatus: {
    type?: string;
    [key: string]: any;
  } = {};
  private accessRequestPending = false;
  private static readonly ACCESS_PENDING_URN = 'urn:dx:apdServerPanel:access-pending';
  private static readonly ACCESS_SUCCESS_URN = 'urn:dx:apdServerPanel:success';
  private static readonly ACCESS_NO_ACCESS_URN = 'urn:dx:apdServerPanel:no-access';
  private static readonly RATING_EDITOR_INTENT_KEY = 'cdpg.openRatingEditor';
  private accessCheckSeq = 0;
  currentUserSub: string | null = null;
  private hasLoadedCurrentUserInfo = false;
  userHasOrganisation = false;
  requestAccessOrganisationName = '';

  activeTab: 'model' | 'details' | 'visualisation' | 'sql-playground' | 'discussion' | 'additional-info' = 'model';
  feedbackFaqRows: AdditionalInfoFaqRow[] = [];
  feedbackInfoText = '';
  expandedFaqIndex: number | null = null;

  private route = inject(ActivatedRoute);

  setActiveTab(tab: 'model' | 'details' | 'visualisation' | 'sql-playground' | 'discussion' | 'additional-info'): void {
    this.activeTab = tab;
    if (tab === 'discussion') {
      this.loadModelDiscussions();
    }
  }

  /** Show Additional Information only when GET feedback returned FAQ and/or INFO. */
  get showAdditionalInfoTab(): boolean {
    return this.feedbackFaqRows.length > 0 || this.feedbackInfoText.trim().length > 0;
  }

  toggleFaq(index: number): void {
    this.expandedFaqIndex = this.expandedFaqIndex === index ? null : index;
  }

  private async loadAdditionalInfo(): Promise<void> {
    const id = this.modelId;
    if (!id) {
      this.feedbackFaqRows = [];
      this.feedbackInfoText = '';
      this.expandedFaqIndex = null;
      this.leaveAdditionalInfoTabIfHidden();
      return;
    }
    const items = await this.catalogueApiService.getProviderFeedback(id);
    if (this.modelId !== id) {
      return;
    }
    const split = splitFeedbackItems(items);
    this.feedbackFaqRows = split.faqRows;
    this.feedbackInfoText = split.infoText;
    this.expandedFaqIndex = split.faqRows.length > 0 ? 0 : null;
    this.leaveAdditionalInfoTabIfHidden();
    this.cdr.detectChanges();
  }

  private leaveAdditionalInfoTabIfHidden(): void {
    if (this.activeTab === 'additional-info' && !this.showAdditionalInfoTab) {
      this.activeTab = 'model';
    }
  }

  get visibleTags(): string[] {
    return this.modelTags.slice(0, this.MAX_VISIBLE_TAGS);
  }

  get remainingTagsCount(): number {
    return Math.max(0, this.modelTags.length - this.MAX_VISIBLE_TAGS);
  }

  get allTags(): string[] {
    return this.modelTags;
  }

  get hasModelDetails(): boolean {
    return this.modelDetails && Object.keys(this.modelDetails).length > 0;
  }

  /** Normalized list of resource servers for display (name, url, queryTypes). */
  get resourceServersList(): any[] {
    const rs =
      this.fullModelData?.resourceServer ?? this.fullModelData?.resource_server;
    if (!rs) {
      return [];
    }
    return Array.isArray(rs) ? rs : [rs];
  }

  resourceServerDesign: 'cards' | 'table' | 'list' = 'table';

  /** True when the model has a user-provided Data Source URL (not catalogue placeholder). */
  get hasRealDataSourceUrl(): boolean {
    const url = this.modelDetails?.mediaUrl;
    if (typeof url !== 'string' || !url.trim()) {
      return false;
    }
    return !this.isPlaceholderMediaUrl(url);
  }

  /** True when mediaUrl exists and is a real external link (GitHub / redirect). */
  get hasMediaUrl(): boolean {
    return this.hasRealDataSourceUrl;
  }

  /** Model was created via file upload (no Data Source URL). */
  get hasUploadedFiles(): boolean {
    return !this.hasRealDataSourceUrl;
  }

  get isOpenAccess(): boolean {
    const badge = (this.modelCardDetails.badge || '').toLowerCase();
    const accessPolicy = String(
      this.fullModelData?.accessPolicy ?? this.fullModelData?.access_policy ?? '',
    ).toUpperCase();
    return badge === 'open' || badge === 'public' || accessPolicy === 'OPEN';
  }

  get showDelegateRequestWarning(): boolean {
    return (
      this.workspaceContext.isDelegateSession() &&
      this.isRestrictedAccess &&
      !this.isModelOwner &&
      !this.isActiveDelegatorAssetOwner()
    );
  }

  /** Asset owned by the user this delegate is acting for. */
  private isActiveDelegatorAssetOwner(): boolean {
    const ownerUserId = (
      this.fullModelData?.ownerUserId ??
      this.fullModelData?.owner_user_id ??
      this.fullModelData?.ownerUserID ??
      ''
    )
      .toString()
      .trim();
    const delegatorId = (this.workspaceContext.getActiveDelegatorId() || '').trim();
    return !!ownerUserId && !!delegatorId && ownerUserId === delegatorId;
  }

  get isRestrictedAccess(): boolean {
    const badge = (this.modelCardDetails.badge || '').toLowerCase();
    const accessPolicy = String(
      this.fullModelData?.accessPolicy ?? this.fullModelData?.access_policy ?? '',
    ).toLowerCase();
    return (
      badge === 'restricted' ||
      accessPolicy === 'restricted' ||
      accessPolicy === 'secure' ||
      accessPolicy === 'private'
    );
  }

  get hasModelAccess(): boolean {
    return (
      this.accessStatus.type === ModelDetailComponent.ACCESS_SUCCESS_URN || this.isModelOwner
    );
  }

  /** Show Download for open models with uploaded files (same rule as dataset detail). */
  get shouldShowDownloadButton(): boolean {
    return this.hasUploadedFiles && this.isOpenAccess && !this.isAssetReadOnly;
  }

  /**
   * Restricted models: enable Download only when has_access passes
   * (`urn:dx:apdServerPanel:success`) or the logged-in user owns the model.
   */
  get shouldShowDownloadButtonForRestricted(): boolean {
    const accessPolicy = String(
      this.fullModelData?.accessPolicy ?? this.fullModelData?.access_policy ?? '',
    ).toUpperCase();
    return (
      this.hasUploadedFiles &&
      this.isRestrictedAccess &&
      accessPolicy !== 'OPEN' &&
      this.hasModelAccess &&
      !this.isAssetReadOnly
    );
  }

  /** Org-admin who owns this model — show Download (provider flow), not Request Access. */
  get isOrgAdminModelOwner(): boolean {
    return this.isOrgAdmin && this.isModelOwner;
  }

  get shouldShowRequestAccessButton(): boolean {
    if (this.workspaceContext.isDelegateSession()) {
      return false;
    }

    // Org-admin owners get Download instead (same as provider owner flow).
    if (this.isOrgAdminModelOwner) {
      return false;
    }

    const accessPolicy = String(
      this.fullModelData?.accessPolicy ?? this.fullModelData?.access_policy ?? '',
    ).toUpperCase();
    if (this.hasModelAccess) {
      return false;
    }

    return this.isRestrictedAccess && accessPolicy !== 'OPEN' && !this.isAssetReadOnly;
  }

  /** GitHub / redirect when open or access granted on restricted models. */
  get shouldShowMediaUrlButton(): boolean {
    return this.hasRealDataSourceUrl && (this.isOpenAccess || this.hasModelAccess) && !this.isAssetReadOnly;
  }

  get requestAccessButtonText(): string {
    return this.isAccessRequestPendingState() ? 'Request Sent' : 'Request Access';
  }

  get isRequestAccessButtonDisabled(): boolean {
    return this.isAccessRequestPendingState();
  }

  get isModelOwner(): boolean {
    const ownerUserId = (
      this.fullModelData?.ownerUserId ??
      this.fullModelData?.owner_user_id ??
      this.fullModelData?.ownerUserID ??
      ''
    )
      .toString()
      .trim();
    const currentSub = this.currentUserSub ?? this.getCurrentUserSubFromKeycloak();
    return !!ownerUserId && !!currentSub && ownerUserId === currentSub;
  }

  /** True if mediaUrl contains github.com (show GitHub Repo); else show Redirect */
  get isMediaUrlGithub(): boolean {
    const url = this.modelDetails?.mediaUrl ?? '';
    return typeof url === 'string' && url.toLowerCase().includes('github.com');
  }

  async ngOnInit(): Promise<void> {
    await this.checkLoginStatus();
    if (this.isLoggedIn) {
      await this.rolesApiService.getRoles();
    }
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          this.isLoggedIn = true;
          await this.checkBookmarkAndLikeStatus();
          void this.checkUserAccess();
          await this.loadUserFeedback();
        } else if (
          keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
          keycloakEvent.type === KeycloakEventType.OnAuthLogout
        ) {
          await this.checkLoginStatus();
          if (keycloakEvent.type === KeycloakEventType.OnAuthLogout) {
            this.isBookmarked = false;
            this.isLiked = false;
            this.accessRequestPending = false;
            this.accessStatus = {};
          }
        }
      }
    );
    this.route.queryParamMap.subscribe((queryParamMap) => {
      this.itemAuditEnabled = parseItemAuditEnabled(queryParamMap);
    });
    this.route.paramMap.subscribe(params => {
      this.modelId = params.get('id');
      this.loadModelDetails();
      void this.loadAdditionalInfo();
    });
  }

  async loadModelDiscussions(): Promise<void> {
    if (!this.modelId) {
      this.modelDiscussions = [];
      return;
    }
    this.modelDiscussionsLoading = true;
    this.modelDiscussionsError = null;
    this.cdr.detectChanges();
    try {
      const response = await this.discussionApiService.getDiscussionChoices({
        filters: {
          type: [],
          status: ['APPROVED'],
          tags: [],
          sub_category_id: this.modelId
        },
        pinned: true,
        sort_by: 'newest',
        page: 1,
        limit: 10
      });
      const discussions = response?.data?.discussions ?? response?.discussions ?? [];
      this.modelDiscussions = (Array.isArray(discussions) ? discussions : []).map((item: any) =>
        this.mapDiscussionChoiceToItem(item)
      );
    } catch (error) {
      console.error('Error loading model discussions:', error);
      this.modelDiscussionsError = 'Failed to load discussions. Please try again.';
      this.modelDiscussions = [];
    } finally {
      this.modelDiscussionsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapDiscussionChoiceToItem(item: any): DiscussionItem {
    const author = item?.user?.name ?? 'Unknown';
    const subCategory = item?.sub_category ?? this.headerConfig.title ?? '';
    const publishedTime = item?.published_time ?? item?.created_at ?? '';
    return {
      id: item?.id ?? '',
      title: item?.title ?? '',
      description: `${author} · ${subCategory}`,
      author,
      category: item?.category ?? 'DATA_BANKS',
      replies: item?.votes ?? 0,
      views: item?.view_count ?? 0,
      lastActivity: publishedTime
        ? `Published ${this.formatService.formatDiscussionActivity(publishedTime)}`
        : '',
      isPinned: item?.is_pinned ?? false,
      isBookmarked: item?.is_bookmarked ?? false,
      isVoted: item?.is_voted ?? false,
      status: item?.status,
      subCategoryId: item?.sub_category_id,
      assetType: 'Model',
      assetName: subCategory
    };
  }

  navigateToDiscussion(discussionId: string): void {
    if (this.isAssetReadOnly) {
      return;
    }

    this.discussionApiService.getReview(discussionId).then(() => {
      this.router.navigate(['/discussions', discussionId]);
    }).catch(() => {
      this.router.navigate(['/discussions', discussionId]);
    });
  }

  createNewDiscussion(): void {
    if (this.isAssetReadOnly) {
      return;
    }

    this.router.navigate(['/discussions/create-new'], {
      queryParams: { category: 'GETTING_STARTED', tab: 'getting-started' }
    });
  }

  ngOnDestroy(): void {
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
  }

  async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  async onBookmarkClick(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.modelId) return;
    try {
      const actionType = this.isBookmarked ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.modelId,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isBookmarked = !this.isBookmarked;
      if (this.isBookmarked) {
        this.toasterService.success("You've bookmarked the Model successfully.");
      } else {
        this.toasterService.success('Bookmark removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }

  async onLikeClick(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }
    if (!this.modelId) return;
    try {
      const actionType = this.isLiked ? 'DISLIKE' : 'LIKE';
      await this.userInteractionApiService.createBookmarkOrLike({
        assetId: this.modelId,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isLiked = !this.isLiked;
      // Optimistically update like count immediately (item API may have eventual consistency)
      this.activityMetrics.likes = Math.max(0, (this.activityMetrics.likes ?? 0) + (this.isLiked ? 1 : -1));
      if (this.isLiked) {
        this.toasterService.success("You've liked the Model successfully.");
      } else {
        this.toasterService.success('Like removed.');
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }

  /** Refresh activity metrics from item API after like */
  private async refreshActivityMetrics(): Promise<void> {
    if (!this.modelId) return;
    try {
      const response = await this.catalogueApiService.getItemById(this.modelId, {
        auditEnabled: false,
      });
      let data = response;
      if (response?.result) data = Array.isArray(response.result) ? response.result[0] : response.result;
      else if (response?.data) data = Array.isArray(response.data) ? response.data[0] : response.data;
      else if (response?.items?.[0]) data = response.items[0];
      else if (Array.isArray(response) && response.length > 0) data = response[0];
      if (!data) return;
      const metrics = data.metrics || data;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = (metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0) + 1;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;
    } catch {
      // Ignore - keep existing metrics
    }
  }

  async checkBookmarkAndLikeStatus(): Promise<void> {
    if (!this.isLoggedIn || !this.modelId) return;
    try {
      let items: any[] = await this.fetchUserInteractionItems({
        assetId: this.modelId,
        assetType: 'AI_MODEL',
      });

      // Fallback when combined query returns no rows
      if (items.length === 0) {
        const [bookmarkItems, likeItems] = await Promise.all([
          this.fetchUserInteractionItems({
            actionType: 'BOOKMARK',
            assetId: this.modelId,
            assetType: 'AI_MODEL',
          }),
          this.fetchUserInteractionItems({
            actionType: 'LIKE',
            assetId: this.modelId,
            assetType: 'AI_MODEL',
          }),
        ]);
        items = [...bookmarkItems, ...likeItems];
      }

      const first = items[0] ?? {};
      const userInteraction = first?.userInteraction;
      const ad = first?.assetDetails ?? first;

      const bookmarked =
        typeof userInteraction?.bookmarked === 'boolean' ? userInteraction.bookmarked :
        typeof first?.isBookmarked === 'boolean' ? first.isBookmarked :
        typeof first?.bookmarked === 'boolean' ? first.bookmarked :
        typeof ad?.bookmarked === 'boolean' ? ad.bookmarked : null;
      if (bookmarked !== null) {
        this.isBookmarked = bookmarked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isBookmarked = actionTypes.includes('BOOKMARK');
      }

      const liked =
        typeof userInteraction?.liked === 'boolean' ? userInteraction.liked :
        typeof first?.isLiked === 'boolean' ? first.isLiked :
        typeof first?.liked === 'boolean' ? first.liked :
        typeof ad?.liked === 'boolean' ? ad.liked : null;
      if (liked !== null) {
        this.isLiked = liked;
      } else {
        const actionTypes = items
          .map((it: any) => (it?.actionType ?? it?.type ?? it?.interactionType ?? '').toString().toUpperCase())
          .filter(Boolean);
        this.isLiked = actionTypes.includes('LIKE');
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking bookmark/like status:', error);
    }
  }

  private async fetchUserInteractionItems(
    queryParams: Record<string, string>
  ): Promise<any[]> {
    const response = await this.userInteractionApiService.userInteractions(queryParams);
    if (response?.result && Array.isArray(response.result)) return response.result;
    if (response?.results && Array.isArray(response.results)) return response.results;
    if (response?.data && Array.isArray(response.data)) return response.data;
    return [];
  }

  async loadModelDetails(): Promise<void> {
    if (!this.modelId) {
      this.isLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = true;
    try {
      console.log('Loading model details for ID:', this.modelId);
      const response = await this.catalogueApiService.getItemById(this.modelId, {
        auditEnabled: this.itemAuditEnabled,
      });
      console.log('API Response received:', response);

      let modelData = response;
      
      // Handle different response structures
      if (response?.result) {
        if (Array.isArray(response.result)) {
          modelData = response.result.length > 0 ? response.result[0] : null;
        } else {
          modelData = response.result;
        }
      } else if (Array.isArray(response) && response.length > 0) {
        modelData = response[0];
      } else if (response && typeof response === 'object' && !response.result) {
        // Response might be the model data directly
        modelData = response;
      }

      if (!modelData) {
        console.error('No model data found in response. Response structure:', JSON.stringify(response, null, 2));
        this.headerConfig.description = 'Failed to load model information. No data found in response.';
        this.isLoading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        return;
      }

      console.log('Model data loaded:', modelData);

      // Extract short description
      let shortDescription = modelData.shortDescription || modelData.summary || '';
      if (!shortDescription && modelData.description) {
        shortDescription = modelData.description.length > 150
          ? modelData.description.substring(0, 150).trim() + '...'
          : modelData.description;
      }

      // Extract organization name
      const organizationName = modelData.provider || modelData.organisation || modelData.organization || modelData.organisationName || '';

      // Update header config
      const modelTitle = modelData.label || modelData.name || 'Model Details';
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Models', route: '/models' },
          { label: modelTitle }
        ],
        title: modelTitle,
        description: shortDescription || 'Loading model information...',
        assetId: this.modelId || modelData.id || ''
      };

      if (this.isLoggedIn) {
        await this.checkBookmarkAndLikeStatus();
      }

      // Extract description
      this.modelDescription = modelData.description || '';

      // Extract tags
      this.modelTags = [];
      if (modelData.tags && Array.isArray(modelData.tags)) {
        this.modelTags = modelData.tags;
      }

      // Extract model details
      const organisationType = modelData.organizationType || modelData.organisationType || 'Public';
      const industry = modelData.industry || '';

      this.modelDetails = {
        uploadedBy: modelData.provider || modelData.organisation || modelData.organization || '',
        organisationType: organisationType,
        industry: industry,
        organizationName: organizationName,
        lastUpdated: this.formatService.formatDate(modelData.itemCreatedAt || modelData.createdAt || modelData.lastUpdated) || '',
        license: this.formatService.resolveCatalogueLicenseDisplay(
          modelData.license || modelData.licence,
          modelData.otherLicense || modelData.other_license,
        ),
        modelType: modelData.modelType || modelData.type || '',
        programmingLanguage: modelData.language || modelData.programmingLanguage || modelData.codeLanguage || '',
        downloadCount: modelData.downloadCount || modelData.downloads || 0,
        viewCount: modelData.viewCount || modelData.views || 0,
        githubRepo: modelData.githubRepo || modelData.repository || '',
        mediaUrl: modelData.mediaURL || modelData.mediaUrl || '',
        publishStatus: modelData.publishStatus || modelData.publish_status || ''
      };

      // Reset to model tab if mediaUrl is present and user was on details/visualisation/sql-playground (tabs hidden)
      if (this.hasMediaUrl && (this.activeTab === 'details' || this.activeTab === 'visualisation' || this.activeTab === 'sql-playground')) {
        this.activeTab = 'model';
      }

      // Store full model data for accessing publishStatus and other fields
      this.fullModelData = modelData;
      this.refreshApiExplorerContext();

      // Populate Activity Overview from Item API metrics
      const metrics = modelData.metrics || modelData;
      this.activityMetrics.likes = metrics.likes ?? metrics.likeCount ?? metrics.like_count ?? 0;
      this.activityMetrics.downloads = metrics.downloads ?? metrics.downloadCount ?? metrics.download_count ?? 0;
      this.activityMetrics.views = (metrics.views ?? metrics.viewCount ?? metrics.view_count ?? 0) + 1;
      this.activityMetrics.bookmarks = metrics.bookmarks ?? metrics.bookmarkCount ?? metrics.bookmark_count ?? 0;

      if (this.isLoggedIn) {
        await this.checkBookmarkAndLikeStatus();
      }

      // Log publishStatus for debugging
      console.log('Model publishStatus:', {
        publishStatus: modelData.publishStatus || modelData.publish_status,
        fullModelDataPublishStatus: this.fullModelData?.publishStatus,
        modelDetailsPublishStatus: this.modelDetails?.['publishStatus']
      });

      const badge = this.helperService.extractAccessBadge(modelData);
      this.modelCardDetails = {
        badge: badge || 'Public',
        createdAt: this.formatService.formatDateInIST(modelData.itemCreatedAt || modelData.createdAt) || '',
        modelType: modelData.modelType || modelData.type || '',
        programmingLanguage: modelData.language || modelData.programmingLanguage || modelData.codeLanguage || '',
        fileFormat: modelData.fileFormat || modelData.format || '',
        downloadCount: modelData.downloadCount || modelData.downloads || 0,
        viewCount: modelData.viewCount || modelData.views || 0
      };

      this.isLoading = false;
      
      // Trigger change detection to ensure view updates
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Load similar models based on tags (after modelTags is set)
      this.loadSimilarModels().catch(err => console.error('Error loading similar models:', err));

      void this.checkUserAccess();
      void this.loadUserFeedback();
      
      // Also trigger change detection in next microtask for robustness
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });

    } catch (error) {
      console.error('Error loading model details:', error);
      this.headerConfig.description = 'Failed to load model information.';
      this.isLoading = false;
      
      // Trigger change detection on error
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  async loadSimilarModels(): Promise<void> {
    if (!this.modelTags || this.modelTags.length === 0) {
      this.similarModels = [];
      return;
    }
    try {
      const searchCriteria: SearchCriteria[] = [
        { field: 'tags', searchType: 'term', values: this.modelTags },
        { field: 'type', searchType: 'term', values: ['adex:AiModel'] }
      ];
      const response = await this.catalogueApiService.searchAllAssets(
        '', [], [], 1, 10, 'name:asc;itemCreatedAt:asc;', searchCriteria
      );
      let items: any[] = [];
      if (Array.isArray(response)) items = response;
      else if (response?.result && Array.isArray(response.result)) items = response.result;
      else if (response?.items && Array.isArray(response.items)) items = response.items;
      else if (response?.data && Array.isArray(response.data)) items = response.data;
      else if (response?.data?.result && Array.isArray(response.data.result)) items = response.data.result;
      else if (response?.data?.items && Array.isArray(response.data.items)) items = response.data.items;
      this.similarModels = items
        .filter((item: any) => (item.id || item['@id'] || '') !== this.modelId)
        .map((item: any) => this.modelCardMapper.mapApiItemToModelCard(item));
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading similar models:', error);
      this.similarModels = [];
    }
  }

  openTagsModal(): void {
    this.showTagsModal = true;
  }

  onGithubRepoClick(): void {
    if (this.isAssetReadOnly) {
      return;
    }

    const mediaURL = this.modelDetails.mediaUrl;
    if (this.hasRealDataSourceUrl && mediaURL) {
      window.open(mediaURL, '_blank', 'noopener,noreferrer');
    } else {
      console.warn('No mediaURL found for model');
    }
  }

  async onDownload(): Promise<void> {
    if (this.isAssetReadOnly) {
      return;
    }

    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    if (!this.modelId) {
      this.toasterService.error('Model ID not available');
      return;
    }

    try {
      const { downloadUrl } = await this.fileApiService.getDatabankDownloadUrl(this.modelId);
      if (!downloadUrl) {
        this.toasterService.error('No download URL received.');
        return;
      }
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${this.modelId}_model.zip`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.activityMetrics.downloads = (this.activityMetrics.downloads ?? 0) + 1;
      this.toasterService.success('Download started successfully');
    } catch (error: unknown) {
      console.error('Error getting model download URL:', error);
      const err = error as { status?: number; error?: { code?: string } };
      if (err?.status === 404 || err?.error?.code === 'RESOURCE_NOT_FOUND') {
        this.toasterService.error('Unable to find the file to download.');
      } else {
        this.toasterService.error('Failed to download file');
      }
    }
  }

  private refreshApiExplorerContext(): void {
    if (!this.modelId) {
      this.apiExplorerContext = null;
      return;
    }
    this.apiExplorerContext = {
      datasetId: this.modelId,
      resourceServers: this.resourceServersList,
      resourceType:
        this.fullModelData?.resourceType ??
        this.modelDetails?.['resourceType'] ??
        this.fullModelData?.resource_type,
      iudxResourceAPIs: this.fullModelData?.iudxResourceAPIs,
    };
  }

  openApiExplorer(): void {
    this.refreshApiExplorerContext();
    this.showApiExplorer = true;
    this.cdr.markForCheck();
  }

  onApiExplorerVisibleChange(visible: boolean): void {
    this.showApiExplorer = visible;
  }

  onApiExplorerLoginRequired(): void {
    this.showApiExplorer = false;
    setTimeout(() => {
      this.showLoginModal = true;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
  }

  private isPlaceholderMediaUrl(url: unknown): boolean {
    const normalized = String(url ?? '').trim().toLowerCase().replace(/\/+$/, '');
    return !normalized || normalized === 'https://iudx.io' || normalized === 'http://iudx.io';
  }

  async checkUserAccess(): Promise<void> {
    if (!this.modelId) {
      return;
    }

    const pendingUrn = ModelDetailComponent.ACCESS_PENDING_URN;
    const noAccessUrn = ModelDetailComponent.ACCESS_NO_ACCESS_URN;
    const successUrn = ModelDetailComponent.ACCESS_SUCCESS_URN;

    const accessPolicy = String(
      this.fullModelData?.accessPolicy ?? this.fullModelData?.access_policy ?? '',
    ).toUpperCase();
    if (accessPolicy === 'OPEN' || !this.isRestrictedAccess) {
      this.accessRequestPending = false;
      this.accessStatus = { type: successUrn };
      return;
    }

    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
      if (!this.isLoggedIn) {
        return;
      }

      const payload = { itemId: this.modelId };
      const seq = ++this.accessCheckSeq;

      const applyAccessStatus = (next: { type?: string }): void => {
        if (seq !== this.accessCheckSeq) return;
        if (!next || typeof next !== 'object') return;
        this.applyPendingAwareAccessStatus(next);
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      };

      try {
        const response = await this.aclApdApiService.hasAccess(payload);
        // A 200 from has_access means the user has access — unless the body
        // explicitly says pending or no-access. That success enables Download
        // on restricted models (`shouldShowDownloadButtonForRestricted`).
        const normalized = normalizeAclAccessStatus(response);
        const accessType = (normalized.type ?? '').trim();
        const resolvedStatus =
          accessType === pendingUrn || accessType === noAccessUrn
            ? normalized
            : { type: successUrn };
        applyAccessStatus(resolvedStatus);
      } catch (err: unknown) {
        const normalized = normalizeAclAccessStatus(err);
        if (Object.keys(normalized).length > 0) {
          applyAccessStatus(normalized);
        } else if (!this.isAccessRequestPendingState()) {
          applyAccessStatus({});
        }
      }
    } catch (error) {
      console.error('Error checking model access:', error);
      if (!this.isAccessRequestPendingState()) {
        this.accessStatus = {};
        this.cdr.markForCheck();
      }
    }
  }

  private isAccessRequestPendingState(): boolean {
    return (
      this.accessRequestPending ||
      this.accessStatus?.type === ModelDetailComponent.ACCESS_PENDING_URN
    );
  }

  private markAccessRequestPending(): void {
    this.accessRequestPending = true;
    this.accessStatus = { type: ModelDetailComponent.ACCESS_PENDING_URN };
  }

  private applyPendingAwareAccessStatus(next: { type?: string }): void {
    const pendingUrn = ModelDetailComponent.ACCESS_PENDING_URN;
    const successUrn = ModelDetailComponent.ACCESS_SUCCESS_URN;
    const noAccessUrn = ModelDetailComponent.ACCESS_NO_ACCESS_URN;
    const nextType = (next?.type ?? '').trim();

    if (nextType === successUrn) {
      this.accessRequestPending = false;
      this.accessStatus = next;
      return;
    }

    if (nextType === pendingUrn) {
      this.accessRequestPending = true;
      this.accessStatus = next;
      return;
    }

    if (this.isAccessRequestPendingState()) {
      if (!nextType || nextType === noAccessUrn) {
        return;
      }
    }

    this.accessStatus = next;
    if (nextType && nextType !== pendingUrn) {
      this.accessRequestPending = false;
    }
  }

  async onRequestAccess(): Promise<void> {
    if (this.isAssetReadOnly || this.isRequestAccessButtonDisabled) {
      return;
    }

    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        this.showLoginModal = true;
        return;
      }
      this.showRequestAccessModal = true;
      await this.loadUserOrganisation();
    } catch {
      this.showLoginModal = true;
    }
  }

  closeRequestAccessModal(): void {
    this.showRequestAccessModal = false;
  }

  async onRequestAccessSubmit(formData: {
    requestAs: string;
    purposeDescription: string;
    agreeToTerms: boolean;
    organisationName?: string;
  }): Promise<void> {
    if (!this.modelId) return;
    await this.createModelAccessRequest({
      requestAs: formData.requestAs,
      purposeDescription: formData.purposeDescription,
      organisationName: formData.organisationName ?? '',
    });
  }

  private async createModelAccessRequest(params: {
    requestAs: string;
    purposeDescription: string;
    organisationName: string;
  }): Promise<void> {
    if (!this.modelId) return;

    try {
      const payload = {
        itemId: this.modelId,
        requestType: 'DOWNLOAD',
        additionalInfo: {
          requestAs: params.requestAs,
          purposeOfRequest: params.purposeDescription,
          organisationName: params.requestAs === 'Organisation' ? params.organisationName : '',
        },
      };
      await this.aclApdApiService.createAccessRequest(payload);
      this.googleAnalytics.trackRequestAccess(this.headerConfig.title ?? '', this.modelId ?? '');
      this.toasterService.success('Your request is sent, Successfully');
      this.closeRequestAccessModal();
      this.markAccessRequestPending();
      this.cdr.markForCheck();
      await this.checkUserAccess();
    } catch (error) {
      console.error('Error creating model access request:', error);
      this.toasterService.error('Failed to send access request. Please try again.');
    }
  }

  async loadUserOrganisation(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      this.setCurrentUserSubFromUserData(userData);
      this.hasLoadedCurrentUserInfo = true;

      if (!userData?.['result']) {
        this.userHasOrganisation = false;
        this.requestAccessOrganisationName = '';
        return;
      }

      const result = userData['result'];
      let organisationStatus: string | null = null;
      let organisationName: string | null = null;

      if (result['organisation']) {
        const org = result['organisation'];
        const rawStatus = org['status'] ?? null;
        organisationStatus = rawStatus ? String(rawStatus).toLowerCase().trim() : null;
        if (organisationStatus !== 'pending') {
          organisationName = org['organisationName'] || org['name'] || null;
        }
      }

      if (!organisationName && organisationStatus !== 'pending') {
        const organisationId = result['organisation']?.['organisationId'] ?? null;
        if (organisationId) {
          const organisationsResponse = await this.authApiService.getAllOrganisations(1, 100);
          let organisations: any[] = [];
          if (Array.isArray(organisationsResponse)) {
            organisations = organisationsResponse;
          } else if (organisationsResponse?.['result']) {
            organisations = Array.isArray(organisationsResponse['result'])
              ? organisationsResponse['result']
              : organisationsResponse['result']['organisations'] || [];
          }
          const matchedOrganisation = organisations.find((org: any) => {
            const orgId = org.id || org.organisationId || org.organisation_id;
            return orgId === organisationId;
          });
          if (matchedOrganisation) {
            organisationName =
              matchedOrganisation.orgName ||
              matchedOrganisation.name ||
              matchedOrganisation.organisationName;
          }
        }
      }

      if (organisationName && organisationStatus !== 'pending') {
        this.requestAccessOrganisationName = organisationName;
        this.userHasOrganisation = true;
      } else {
        this.requestAccessOrganisationName = '';
        this.userHasOrganisation = false;
      }
    } catch (error) {
      console.error('Error loading user organisation:', error);
      this.requestAccessOrganisationName = '';
      this.userHasOrganisation = false;
    }
  }

  private setCurrentUserSubFromUserData(userData: any): void {
    const result = userData?.['result'] ?? userData;
    const sub = result?.['sub'] ?? this.getCurrentUserSubFromKeycloak();
    this.currentUserSub = typeof sub === 'string' && sub.trim() ? sub.trim() : null;
  }

  private getCurrentUserSubFromKeycloak(): string | null {
    try {
      const instance = this.keycloak.getKeycloakInstance() as { tokenParsed?: { sub?: string }; subject?: string };
      const sub = instance?.tokenParsed?.sub ?? instance?.subject ?? null;
      return typeof sub === 'string' && sub.trim() ? sub.trim() : null;
    } catch {
      return null;
    }
  }

  /** True when the logged-in user holds the org-admin role. */
  get isOrgAdmin(): boolean {
    return this.rolesApiService.hasRole(Role.ORG_ADMIN);
  }

  get isPublishStatusPending(): boolean {
    return (
      this.helperService.isPublishStatusPending(this.fullModelData) ||
      this.helperService.isPublishStatusPending(this.modelDetails)
    );
  }

  get isPublishStatusDeclined(): boolean {
    return (
      this.helperService.isPublishStatusRejected(this.fullModelData) ||
      this.helperService.isPublishStatusRejected(this.modelDetails)
    );
  }

  /** True when itemStatus is DECLINED / REJECTED (Organisation Admin rejection signal). */
  get isItemStatusDeclined(): boolean {
    return (
      this.helperService.isItemStatusRejected(this.fullModelData) ||
      this.helperService.isItemStatusRejected(this.modelDetails)
    );
  }

  /** True when itemStatus is UNVERIFIED / UNVERIFIED-SFTP / UNVERIFIED-API. */
  get isItemStatusUnverified(): boolean {
    return (
      this.helperService.isItemStatusUnverified(this.fullModelData) ||
      this.helperService.isItemStatusUnverified(this.modelDetails)
    );
  }

  /** Org Admin has not approved yet. */
  get isOrgAdminApprovalPending(): boolean {
    return this.isItemStatusUnverified;
  }

  /** Platform Admin has not approved yet. */
  get isPlatformAdminApprovalPending(): boolean {
    return this.isPublishStatusPending;
  }

  /**
   * Role name(s) still pending approval for the banner message.
   */
  get pendingApprovalRolesLabel(): string {
    const orgPending = this.isOrgAdminApprovalPending;
    const platformPending = this.isPlatformAdminApprovalPending;
    if (orgPending && platformPending) {
      return 'the Organisation administrator and Platform Administrator';
    }
    if (orgPending) {
      return 'the Organisation administrator';
    }
    if (platformPending) {
      return 'the Platform Administrator';
    }
    return 'the Organisation administrator and Platform Administrator';
  }

  /**
   * Yellow pending banner for the asset owner only (not org/platform admin reviewers
   * or other viewers).
   */
  get showPendingApprovalBanner(): boolean {
    if (!this.isModelOwner || this.rejectionBannerMessage) {
      return false;
    }
    return this.isOrgAdminApprovalPending || this.isPlatformAdminApprovalPending;
  }

  /** User Ratings are for Active assets only. Hidden while Pending or Rejected. */
  get showUserRatingsSection(): boolean {
    if (
      this.isItemStatusDeclined ||
      this.isItemStatusUnverified ||
      this.isPublishStatusDeclined ||
      this.isPublishStatusPending
    ) {
      return false;
    }
    const item = this.fullModelData ?? this.modelDetails;
    return (
      !!item &&
      this.helperService.isItemStatusVerified(item) &&
      this.helperService.isPublishStatusApproved(item) &&
      this.helperService.isDataUploadComplete(item)
    );
  }

  /**
   * Yellow-bar rejection message:
   * - itemStatus DECLINED → Organisation Admin (metadata/publish-assets reject)
   * - publishStatus DECLINED → Platform Admin (manage platform assets reject)
   */
  get rejectionBannerMessage(): string | null {
    if (this.isItemStatusDeclined) {
      return 'This model has been rejected by the Organization Admin.';
    }
    if (this.isPublishStatusDeclined) {
      return 'This model has been rejected by the Platform Admin.';
    }
    return null;
  }

  /** Strict read-only mode for pending or rejected models. */
  get isAssetReadOnly(): boolean {
    return (
      this.isPublishStatusPending ||
      this.isPublishStatusDeclined ||
      this.isItemStatusDeclined ||
      this.isItemStatusUnverified
    );
  }

  /** Date part from formatted string (e.g. "02-Feb-2026") - match Dataset details */
  getCreatedAtDate(createdAt: string): string {
    if (!createdAt) return '';
    const parts = createdAt.split(' ');
    return parts[0] || '';
  }

  /** Time part from formatted string (e.g. "11:43 IST") - match Dataset details */
  getCreatedAtTime(createdAt: string): string | null {
    if (!createdAt) return null;
    const parts = createdAt.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : null;
  }

  /** Platform admins and delegates do not submit ratings on the catalogue. */
  get canPerformUserFeedback(): boolean {
    return (
      !this.rolesApiService.hasRole(Role.ADMIN) &&
      !this.workspaceContext.isDelegateSession()
    );
  }

  /** Open models, the owner, or a restricted model after access is granted. */
  get canProvideModelRating(): boolean {
    if (this.isOpenAccess || this.isModelOwner) {
      return true;
    }
    return this.hasModelAccess;
  }

  get showAccessRequiredRatingState(): boolean {
    return this.isRestrictedAccess && !this.canProvideModelRating;
  }

  async loadUserFeedback(): Promise<void> {
    if (!this.modelId) {
      this.userFeedbackItems = [];
      this.userFeedbackSummary = null;
      this.myModelReview = null;
      this.ownSubmittedReview = null;
      return;
    }

    const request = ++this.userFeedbackRequest;
    const rating = this.userRatingsFilter;
    try {
      const ownPromise = this.fetchOwnModelFeedback();
      const response = await this.userInteractionApiService.getApprovedUserFeedback({
        assetId: this.modelId,
        page: 1,
        size: 100,
        sort: this.userRatingsSort === 'new' ? 'ratingCreatedAt:desc' : 'ratingCreatedAt:asc',
        ...(rating != null ? { rating } : {}),
      });
      const own = await ownPromise;
      if (request !== this.userFeedbackRequest) {
        return;
      }
      this.ownSubmittedReview = own;
      const items = this.extractFeedbackList(response);
      const mapped = items.map((item) => this.mapApprovedModelReview(item));
      const mine = this.findMyModelReview(mapped);
      if (mine) {
        this.myModelReview = mine;
      } else if (rating == null) {
        this.myModelReview = null;
      }
      const hasExplicitStatus = items.some((item) =>
        this.hasExplicitReviewStatus(this.asFeedbackRecord(item)),
      );
      this.userFeedbackItems = hasExplicitStatus
        ? mapped.filter((item) => item.status === 'approved')
        : mapped.filter((item) => item.status !== 'rejected');
      if (rating == null) {
        this.userFeedbackSummary =
          this.extractFeedbackSummary(response) ?? this.summarizeModelReviews(this.userFeedbackItems);
      }
      this.userRatingsPage = 0;
      this.cdr.detectChanges();
      this.openPendingRatingEditorAfterLogin();
    } catch (error) {
      if (request !== this.userFeedbackRequest) {
        return;
      }
      console.error('Error loading model feedback:', error);
      this.userFeedbackItems = [];
      if (rating == null) {
        this.userFeedbackSummary = null;
        this.myModelReview = null;
      }
    }
  }

  get hasUserRatings(): boolean {
    return (this.userFeedbackSummary?.totalRatings ?? 0) > 0 || this.userFeedbackItems.length > 0;
  }

  get hasOwnRating(): boolean {
    return !!this.myModelReview;
  }

  /** Edit is only for a rating that is still pending. An approved rating stays read-only. */
  get hasOwnPendingRating(): boolean {
    if (this.myModelReview?.status === 'approved' || this.ownSubmittedReview?.status === 'approved') {
      return false;
    }
    return (this.ownSubmittedReview?.status ?? this.myModelReview?.status) === 'pending';
  }

  get blockNewRating(): boolean {
    const status = this.ownSubmittedReview?.status ?? this.myModelReview?.status;
    return status === 'pending' || status === 'approved';
  }

  get giveRatingCtaLabel(): string {
    if (this.hasOwnPendingRating) {
      return 'Edit Your Rating';
    }
    return this.hasOwnRating ? 'View your rating' : 'Give your Rating';
  }

  get giveRatingModalHeader(): string {
    if (this.isEditingOwnRating) {
      return 'Edit Your Rating';
    }
    return this.isViewingOwnRating ? 'View your Rating' : 'Give your Rating';
  }

  get userRatingsFilterLabel(): string {
    if (this.userRatingsFilter == null) {
      return 'All ratings';
    }
    return this.userRatingsFilter === 1 ? '1 star' : `${this.userRatingsFilter} stars`;
  }

  get filteredUserReviews(): ModelReviewItem[] {
    return this.userFeedbackItems;
  }

  get userRatingsPageCount(): number {
    return Math.max(1, Math.ceil(this.filteredUserReviews.length / this.userRatingsPageSize));
  }

  get userRatingsPageIndexes(): number[] {
    return Array.from({ length: this.userRatingsPageCount }, (_, index) => index);
  }

  get visibleUserReviews(): ModelReviewItem[] {
    const maxPage = this.userRatingsPageCount - 1;
    const page = Math.min(Math.max(this.userRatingsPage, 0), maxPage);
    const start = page * this.userRatingsPageSize;
    return this.filteredUserReviews.slice(start, start + this.userRatingsPageSize);
  }

  get userRatingsAverage(): number | null {
    return this.userFeedbackSummary?.averageRating ?? this.summarizeModelReviews(this.userFeedbackItems)?.averageRating ?? null;
  }

  get userRatingsCount(): number {
    return this.userFeedbackSummary?.totalRatings ?? this.userFeedbackItems.length;
  }

  get userRatingsAverageDisplay(): string {
    const rating = this.userRatingsAverage;
    if (rating == null) {
      return '—';
    }
    return Number.isInteger(rating) ? String(rating) : rating.toFixed(1);
  }

  userRatingsStarFilled(star: number, value: number | null = this.userRatingsAverage): boolean {
    return (value ?? 0) >= star;
  }

  ratingStarFillPercent(star: number): number {
    return ratingStarFraction(star, this.userRatingsAverage) * 100;
  }

  setUserRatingsSort(sort: 'new' | 'old'): void {
    if (this.userRatingsSort === sort) {
      return;
    }
    this.userRatingsSort = sort;
    this.userRatingsPage = 0;
    void this.loadUserFeedback();
  }

  toggleUserRatingsSort(): void {
    this.setUserRatingsSort(this.userRatingsSort === 'new' ? 'old' : 'new');
  }

  toggleUserRatingsFilterMenu(): void {
    this.showUserRatingsFilterMenu = !this.showUserRatingsFilterMenu;
  }

  setUserRatingsFilter(stars: number | null): void {
    this.showUserRatingsFilterMenu = false;
    if (this.userRatingsFilter === stars) {
      return;
    }
    this.userRatingsFilter = stars;
    this.userRatingsPage = 0;
    void this.loadUserFeedback();
  }

  goToUserRatingsPage(page: number): void {
    const max = this.userRatingsPageCount - 1;
    this.userRatingsPage = Math.min(Math.max(page, 0), max);
  }

  async onGiveRating(): Promise<void> {
    if (!this.canPerformUserFeedback) {
      return;
    }
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch {
      this.isLoggedIn = false;
    }
    if (!this.isLoggedIn) {
      this.storeRatingEditorIntent();
      this.showLoginModal = true;
      this.cdr.detectChanges();
      return;
    }
    if (this.hasOwnPendingRating) {
      this.openOwnRatingEditor();
      return;
    }
    const ownReview = this.myModelReview ?? this.ownSubmittedReview;
    if (ownReview && ownReview.status !== 'rejected') {
      this.openOwnRatingView(ownReview);
      return;
    }
    this.isViewingOwnRating = false;
    this.isEditingOwnRating = false;
    this.resetGiveRatingForm();
    this.showGiveRatingModal = true;
    this.cdr.detectChanges();
  }

  closeGiveRatingModal(): void {
    this.showGiveRatingModal = false;
    this.isViewingOwnRating = false;
    this.isEditingOwnRating = false;
    this.resetGiveRatingForm();
  }

  onGiveRatingModalVisibleChange(visible: boolean): void {
    this.showGiveRatingModal = visible;
    if (!visible) {
      this.isViewingOwnRating = false;
      this.isEditingOwnRating = false;
      this.resetGiveRatingForm();
    }
  }

  selectRating(value: number): void {
    if (this.isViewingOwnRating) {
      return;
    }
    this.selectedRating = value;
  }

  get displayedRating(): number {
    return this.hoveredRating || this.selectedRating;
  }

  get ratingFeedbackLength(): number {
    return this.ratingFeedback.length;
  }

  get showRatingRequiredError(): boolean {
    return this.ratingFormSubmitted && this.selectedRating < 1;
  }

  async submitGiveRating(): Promise<void> {
    if (!this.canPerformUserFeedback) {
      return;
    }
    this.ratingFormSubmitted = true;
    if (this.selectedRating < 1) {
      this.toasterService.error('Please select a rating');
      return;
    }
    if (!this.modelId) {
      this.toasterService.error('Unable to submit rating: model id is missing');
      return;
    }
    if (this.isSubmittingRating || (this.blockNewRating && !this.isEditingOwnRating)) {
      return;
    }
    if (this.isEditingOwnRating && !this.hasOwnPendingRating) {
      return;
    }
    if (this.isEditingOwnRating && !this.canSaveRatingEdit) {
      return;
    }

    this.isSubmittingRating = true;
    this.cdr.detectChanges();
    try {
      const comment = this.ratingFeedback.trim().slice(0, 200);
      if (this.isEditingOwnRating) {
        const payload: {
          assetId: string;
          assetType: string;
          entityRating: number;
          actionSubtype?: string;
          actionSubdata?: Record<string, unknown>;
        } = {
          assetId: this.modelId,
          assetType: 'AI_MODEL',
          entityRating: this.selectedRating,
        };
        if (comment) {
          payload.actionSubtype = 'feedback';
          payload.actionSubdata = { comment };
        }
        await this.userInteractionApiService.updateUserFeedback(payload);
        this.toasterService.success('Feedback updated.');
      } else {
        await this.userInteractionApiService.postUserFeedback({
          assetId: this.modelId,
          assetType: 'AI_MODEL',
          entityRating: this.selectedRating,
          actionSubtype: '',
          actionSubdata: {
            comment,
          },
        });
        this.toasterService.success('Rating submitted. It will appear after platform admin approval.');
      }
      this.closeGiveRatingModal();
      await this.loadUserFeedback();
    } catch (error: unknown) {
      console.error('Error submitting model rating:', error);
      const httpError = error as {
        status?: number;
        error?: { detail?: string; type?: string };
      };
      const isConflict =
        httpError?.status === 409 || String(httpError?.error?.type ?? '').includes('conflict');
      if (isConflict) {
        this.toasterService.error(
          httpError?.error?.detail?.trim() || 'Feedback already exists for this asset.',
        );
      }
    } finally {
      this.isSubmittingRating = false;
      this.cdr.detectChanges();
    }
  }

  get canSaveRatingEdit(): boolean {
    return (
      this.selectedRating !== this.editBaselineRating ||
      this.ratingFeedback.trim() !== this.editBaselineFeedback.trim()
    );
  }

  private resetGiveRatingForm(): void {
    this.selectedRating = 0;
    this.hoveredRating = 0;
    this.ratingFeedback = '';
    this.editBaselineRating = 0;
    this.editBaselineFeedback = '';
    this.ratingFormSubmitted = false;
  }

  private openOwnRatingView(review: ModelReviewItem): void {
    this.isViewingOwnRating = true;
    this.isEditingOwnRating = false;
    this.selectedRating = review.ratingValue;
    this.hoveredRating = 0;
    this.ratingFeedback = review.comment;
    this.ratingFormSubmitted = false;
    this.showGiveRatingModal = true;
    this.cdr.detectChanges();
  }

  private openOwnRatingEditor(): void {
    const review = this.ownSubmittedReview ?? this.myModelReview;
    if (!review || !this.hasOwnPendingRating) {
      if (review) {
        this.openOwnRatingView(review);
      }
      return;
    }
    this.isViewingOwnRating = false;
    this.isEditingOwnRating = true;
    this.selectedRating = review.ratingValue;
    this.hoveredRating = 0;
    this.ratingFeedback = review.comment;
    this.editBaselineRating = review.ratingValue;
    this.editBaselineFeedback = review.comment ?? '';
    this.ratingFormSubmitted = false;
    this.showGiveRatingModal = true;
    this.cdr.detectChanges();
  }

  private storeRatingEditorIntent(): void {
    if (!this.hasOwnPendingRating || !this.modelId) {
      return;
    }
    try {
      sessionStorage.setItem(ModelDetailComponent.RATING_EDITOR_INTENT_KEY, this.modelId);
    } catch {
      /* private mode */
    }
  }

  private openPendingRatingEditorAfterLogin(): void {
    if (!this.canPerformUserFeedback || !this.modelId || !this.hasOwnPendingRating) {
      return;
    }
    try {
      const intended = sessionStorage.getItem(ModelDetailComponent.RATING_EDITOR_INTENT_KEY);
      if (intended !== this.modelId) {
        return;
      }
      sessionStorage.removeItem(ModelDetailComponent.RATING_EDITOR_INTENT_KEY);
    } catch {
      return;
    }
    this.openOwnRatingEditor();
  }

  private async fetchOwnModelFeedback(): Promise<ModelReviewItem | null> {
    try {
      if (!(await this.keycloak.isLoggedIn()) || !this.modelId) {
        return null;
      }
      const response = await this.userInteractionApiService.getMyUserFeedback({
        assetId: this.modelId,
        page: 1,
        size: 100,
      });
      return (
        this.extractFeedbackList(response)
          .map((item) => ({
            source: this.asFeedbackRecord(item),
            review: this.mapModelReview(item),
          }))
          .filter(({ source }) => {
            const assetId = this.readFeedbackString(source, ['assetId', 'asset_id']);
            return !assetId || assetId === this.modelId;
          })
          .map(({ review }) => review)
          .find((review) => review.status === 'pending' || review.status === 'approved') ?? null
      );
    } catch {
      return this.ownSubmittedReview;
    }
  }

  private findMyModelReview(items: ModelReviewItem[]): ModelReviewItem | null {
    const userId = this.currentUserSub ?? this.getCurrentUserSubFromKeycloak();
    const email = (this.getCurrentUserEmailFromKeycloak() ?? '').toLowerCase();
    return (
      items.find((item) => {
        if (userId && item.userId && item.userId === userId) {
          return true;
        }
        return !!email && (item.email.toLowerCase() === email || item.name.toLowerCase() === email);
      }) ?? null
    );
  }

  private getCurrentUserEmailFromKeycloak(): string | null {
    try {
      const instance = this.keycloak.getKeycloakInstance() as {
        tokenParsed?: { email?: string; preferred_username?: string };
      };
      const email = instance?.tokenParsed?.email ?? instance?.tokenParsed?.preferred_username;
      return typeof email === 'string' && email.includes('@') ? email.trim() : null;
    } catch {
      return null;
    }
  }

  /** Rows from the approved-feedback API are published even when status is omitted. */
  private mapApprovedModelReview(item: unknown): ModelReviewItem {
    const review = this.mapModelReview(item);
    if (!this.hasExplicitReviewStatus(this.asFeedbackRecord(item))) {
      review.status = 'approved';
    }
    return review;
  }

  private hasExplicitReviewStatus(source: Record<string, unknown>): boolean {
    return !!this.readFeedbackString(source, [
      'feedbackStatus',
      'status',
      'approvalStatus',
      'requestStatus',
    ]);
  }

  private mapModelReview(item: unknown): ModelReviewItem {
    const source = this.asFeedbackRecord(item);
    const user = this.asFeedbackRecord(source['user'] ?? source['consumer'] ?? source['createdBy']);
    const name =
      this.readFeedbackString(source, ['userName', 'consumerName', 'fullName', 'name']) ||
      this.readFeedbackString(user, ['name', 'fullName', 'userName']) ||
      this.readFeedbackString(source, ['userEmail', 'email']) ||
      'Anonymous';
    const email =
      this.readFeedbackString(source, ['userEmail', 'email']) ||
      this.readFeedbackString(user, ['email', 'userEmail']);
    const comment =
      this.readFeedbackString(source, ['comment', 'feedback', 'userFeedback', 'review']) ||
      this.readFeedbackCommentFromSubdata(source['actionSubdata']);
    const createdAtRaw = this.readFeedbackString(source, [
      'ratingCreatedAt',
      'ratingUpdatedAt',
      'createdAt',
      'created_at',
      'updatedAt',
      'submittedAt',
    ]);
    const createdAtMs = createdAtRaw ? new Date(createdAtRaw).getTime() : 0;
    const ratingValue = Number(source['entityRating'] ?? source['rating'] ?? source['stars'] ?? 0);
    return {
      id: this.readFeedbackString(source, ['id', 'feedbackId', '_id']),
      userId:
        this.readFeedbackString(source, ['userId', 'user_id']) ||
        this.readFeedbackString(user, ['id', 'userId', 'sub']),
      email,
      name,
      organisation: this.readFeedbackString(source, ['organisation', 'orgName', 'organization']),
      initials: this.reviewInitials(name),
      comment,
      ratingValue: Number.isFinite(ratingValue) ? ratingValue : 0,
      createdAt: createdAtRaw ? this.formatService.formatDate(createdAtRaw) : '',
      createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
      status: this.readReviewStatus(source),
    };
  }

  private readReviewStatus(source: Record<string, unknown>): 'pending' | 'approved' | 'rejected' {
    const raw = this.readFeedbackString(source, [
      'feedbackStatus',
      'status',
      'approvalStatus',
      'requestStatus',
    ]).toLowerCase();
    if (raw === 'approved' || raw === 'granted') {
      return 'approved';
    }
    if (raw === 'rejected' || raw === 'denied') {
      return 'rejected';
    }
    return 'pending';
  }

  private reviewInitials(name: string): string {
    const local = name.includes('@') ? name.split('@')[0] : name;
    const parts = local.replace(/[._-]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return local.slice(0, 2).toUpperCase() || 'NA';
  }

  private extractFeedbackList(response: unknown): unknown[] {
    const record = this.asFeedbackRecord(response);
    if (Array.isArray(record['result'])) {
      return record['result'];
    }
    const nestedResult = this.asFeedbackRecord(record['result']);
    if (Array.isArray(nestedResult['data'])) {
      return nestedResult['data'];
    }
    if (Array.isArray(record['results'])) {
      return record['results'];
    }
    if (Array.isArray(record['data'])) {
      return record['data'];
    }
    return Array.isArray(response) ? response : [];
  }

  private extractFeedbackSummary(
    response: unknown,
  ): { averageRating: number | null; totalRatings: number } | null {
    const record = this.asFeedbackRecord(response);
    const nestedResult = this.asFeedbackRecord(record['result']);
    const summary = this.asFeedbackRecord(nestedResult['summary'] ?? record['summary']);
    const averageRating = Number(summary['averageRating'] ?? summary['average_rating']);
    const totalRatings = Number(summary['totalRatings'] ?? summary['total_ratings'] ?? summary['totalCount']);
    if (!Number.isFinite(averageRating) && !Number.isFinite(totalRatings)) {
      return null;
    }
    return {
      averageRating: Number.isFinite(averageRating) ? averageRating : null,
      totalRatings: Number.isFinite(totalRatings) ? totalRatings : 0,
    };
  }

  private summarizeModelReviews(
    rows: ModelReviewItem[],
  ): { averageRating: number | null; totalRatings: number } | null {
    const rated = rows.filter((row) => row.ratingValue > 0);
    if (!rated.length) {
      return null;
    }
    const total = rated.reduce((sum, row) => sum + row.ratingValue, 0);
    return { averageRating: total / rated.length, totalRatings: rated.length };
  }

  private readFeedbackCommentFromSubdata(value: unknown): string {
    if (typeof value === 'string') {
      try {
        return this.readFeedbackCommentFromSubdata(JSON.parse(value) as unknown);
      } catch {
        return value.trim();
      }
    }
    return this.readFeedbackString(this.asFeedbackRecord(value), ['comment', 'feedback', 'text', 'message']);
  }

  private readFeedbackString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return '';
  }

  private asFeedbackRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }
}
